import { useMemo } from 'react';
import type { DimensionTab } from '../types';
import { useSwipeTabs } from '../../../shared/hooks';

interface DimensionTabsProps {
  tabs: DimensionTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  dimensionCounts: Record<string, number>;
  /** Optional: Content element to apply swipe visual feedback to */
  children?: React.ReactNode;
  /** Whether to enable swipe gestures (default: true) */
  enableSwipe?: boolean;
}

export function DimensionTabs({
  tabs,
  activeTab,
  onTabChange,
  dimensionCounts,
  children,
  enableSwipe = true,
}: DimensionTabsProps): JSX.Element {
  // Extract tab IDs for the swipe hook
  const tabIds = useMemo(() => tabs.map((t) => t.id), [tabs]);

  // Initialize swipe gesture hook
  const { bind, swipeProgress, isSwiping, prefersReducedMotion } = useSwipeTabs({
    tabs: tabIds,
    activeTab,
    onTabChange,
    threshold: 50,
    edgeMargin: 20,
  });

  // Calculate visual feedback transform
  const contentTransform = useMemo(() => {
    if (prefersReducedMotion || !isSwiping) {
      return {};
    }

    // swipeProgress: negative = swiping left (next), positive = swiping right (prev)
    // Apply a subtle translation during swipe (max 30px)
    const translateX = swipeProgress * -30;

    return {
      transform: `translateX(${translateX}px)`,
      transition: isSwiping ? 'none' : 'transform 150ms ease-out',
    };
  }, [swipeProgress, isSwiping, prefersReducedMotion]);

  return (
    <div className="relative">
      {/* Tab header - uses touch-action: pan-y to prevent horizontal scroll conflict */}
      <div
        className="flex gap-1 border-b-2 border-brand-dark-lighter mb-4 overflow-x-auto touch-pan-y"
        role="tablist"
      >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const count = tab.key ? dimensionCounts[tab.key] : null;

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              onClick={() => onTabChange(tab.id)}
              className={`
                flex items-center gap-2 py-3 px-4 border-none bg-transparent cursor-pointer text-sm whitespace-nowrap -mb-0.5 transition-all duration-150 font-display
                min-h-[44px] min-w-[44px]
                ${isActive
                  ? 'border-b-[3px] border-brand-primary text-brand-primary font-semibold'
                  : 'border-b-[3px] border-transparent text-brand-gray hover:text-white'
                }
              `}
            >
              <span>{tab.icon}</span>
              {tab.label}
              {count !== null && count > 0 && (
                <span className={`
                  text-[0.7rem] py-0.5 px-1.5 rounded-full
                  ${isActive
                    ? 'bg-brand-primary/20 text-brand-primary'
                    : 'bg-brand-dark-lighter text-brand-gray'
                  }
                `}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Swipeable content area - only rendered if children are provided */}
      {children && enableSwipe && (
        <div
          {...bind()}
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={activeTab}
          className="touch-pan-y"
          style={contentTransform}
        >
          {children}
        </div>
      )}

      {/* Non-swipeable content area - when swipe is disabled */}
      {children && !enableSwipe && (
        <div
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={activeTab}
        >
          {children}
        </div>
      )}
    </div>
  );
}
